import { useContext, useEffect } from "react";

import { useRouter } from "next/router";

import useTranslation from "next-translate/useTranslation";

import { ComponentErrorBoundary } from "shared/components/atoms/ComponentErrorBoundary";
import ExclusivePage from "shared/components/organisms/ExclusivePage";
import AuthContext from "shared/providers/context/AuthContext";
import GlobalContext from "shared/providers/context/GlobalContext";
import { useFeature } from "shared/utils/hooks/useFeature";
import useSEO from "shared/utils/hooks/useSEO";
import staticIntlTranslatorToString from "shared/utils/staticIntlTranslatorToString";

import { captureException, captureMessage } from "@sentry/nextjs";

import ContentSections from "./components/ContentSections";
import Header from "./components/Header";
import { CourseLandingPageContainer } from "./styles";
import { checkUserHasAccess } from "./utils/checkUserHasAccess";

export default function CourseLandingPage({ course, reviews, courses }) {
  const { lang } = useTranslation();
  const { state } = useContext(GlobalContext);
  const { loggedUser: user } = useContext(AuthContext);

  const courseAccess = state?.tenancy?.settings?.courses.coursesAreaAccess;
  const userHasAccess = checkUserHasAccess(course, user);
  const { definePageSeo } = useSEO();

  const router = useRouter();
  const isCoursesEnabled = useFeature("f_courses");

  useEffect(() => {
    try {
      const wrapper = document.querySelector(
        ".main-content-wrapper",
      ) as HTMLElement;
      wrapper.style.padding = "0";

      return () => {
        wrapper.style.padding = "32px 32px 0";
      };
    } catch (error) {
      const errorMessage = `Erro (CourseLandingPage | useEffect | document.querySelector): ${error}`;

      console.log(errorMessage);
      captureException(error);
      captureMessage(errorMessage);
    }
  }, []);

  useEffect(() => {
    try {
      if (course) {
        definePageSeo({
          ...course,
          seo_title: course.seo_title,
          seo_description: course.seo_description,
          seo_keywords: course.seo_keyword,
          defaultTitle: staticIntlTranslatorToString(course.title, lang),
        });
      }

      if (course?.status === "inactive" || course?.status === "hided") {
        const robotsMetaTag = document.createElement("meta");
        robotsMetaTag.setAttribute("name", "robots");
        robotsMetaTag.setAttribute("content", "noindex");
        document.head.insertAdjacentElement("beforeend", robotsMetaTag);
      }

      return () => {
        if (course?.status === "inactive" || course?.status === "hided") {
          const noIndexMetaTag = document.querySelector(
            'meta[name="robots"]',
          ) as HTMLMetaElement;
          if (noIndexMetaTag && noIndexMetaTag.content === "noindex")
            noIndexMetaTag.remove();
        }
      };
    } catch (error) {
      const errorMessage = `Erro (CourseLandingPage | useEffect | definePageSeo): ${error}`;

      console.log(errorMessage);
      captureException(error);
      captureMessage(errorMessage);
    }
  }, [course]);

  if (!isCoursesEnabled) router.push("/404");

  return (
    <>
      {!userHasAccess || (courseAccess === "logged_users" && !user) ? (
        <ExclusivePage />
      ) : (
        course &&
        reviews && (
          <CourseLandingPageContainer className="single-course-bg">
            <ComponentErrorBoundary>
              <Header course={course} reviews={reviews} />
            </ComponentErrorBoundary>
            <ContentSections
              course={course}
              reviews={reviews}
              courses={courses}
            />
          </CourseLandingPageContainer>
        )
      )}
    </>
  );
}
